import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { Injector, ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { ChoiceSliderComponent } from './component/choice-slider/choice-slider.component';
import { LightboxComponent } from './component/lightbox/lightbox.component';
import { FormErrorsDirective } from './directive/form-errors.directive';
import { LazyImgDirective } from './directive/lazy-img/lazy-img.directive';
import { SmoothInDirective } from './directive/smooth-in/smooth-in.directive';
import { ENVIRONMENT_TOKEN } from './injection.tokens';
import { AcceptLanguageInterceptor } from './interceptor/accept-language.interceptor';
import { JwtRefreshTokenInterceptor } from './interceptor/jwt-refresh-token.interceptor';
import { JwtTokenInterceptor } from './interceptor/jwt-token.interceptor';
import { RequestInterceptor } from './interceptor/request.interceptor';
import { ResponseInterceptor } from './interceptor/response.interceptor';
import { DateTimePipe } from './pipe/date-time.pipe';
import { MoneyPipe } from './pipe/money.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { ScreenTicketCountSelectorComponent } from './component/screen/ticket-count-selector/ticket-count-selector.component';
import { TicketCountGeneralAdmissionComponent } from './component/screen/ticket-count-general-admission/ticket-count-general-admission.component';
import { ScreenSeatsSelectedListComponent } from './component/screen/seats-selected-list/seats-selected-list.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { PinchZoomComponent } from './component/pinch-zoom/pinch-zoom.component';
import { VarDirective } from './directive/ng-var.directive';
import { CarouselSwipeDirective } from './directive/carousel-swipe.directive';
import { CarouselModule as OwlCarouselModule } from 'ngx-owl-carousel-o';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { PasswordInputComponent } from './component/password-input/password-input.component';
import { DividerComponent } from './component/divider/divider.component';
import { RerenderDirective } from './directive/rerender.directive';
import { PlayWatermarkDirective } from './directive/play-watermark/play-watermark.directive';
import { DigitDirective } from './directive/digit-only.directive';
import { FieldJumperDirective } from './directive/field-jumper.directive';
import { HideOnErrorDirective } from './directive/hide-on-error.directive';
import { PlaceholderImgDirective } from './directive/placeholder-img/placeholder-img.directive';
import { AddLanguagePipe } from './pipe/add-lang.pipe';
import { ExtendedDatePipe } from './pipe/extended-date.pipe';
import { ReplaceClassDirective } from './directive/replace-class.directive';
import { NgPipesModule } from 'ngx-pipes';
import { ScreenSizeDetectorComponent } from './component/size-detector/size-detector.component';
import { EmbedVideoService } from './service/embed-video/embed-video.service';
import { NoindexPageComponent } from './component/noindex-page/noindex-page.component';
import { MoneyDirective } from './directive/money.directive';
import { FitTextToLinesDirective } from './directive/fitTextToLines.directive';
import { OnlyLettersDirective } from './directive/only-letters.directive';
import { ECommerceModule } from './module/e-commerce/e-commerce.module';
import { SocialLoginModule } from './module/auth/sociallogin.module';
import { ScaleToFitDirective } from './directive/scale-to-fit.directive';

@NgModule({
  declarations: [
    DateTimePipe,
    LazyImgDirective,
    PlayWatermarkDirective,
    FormErrorsDirective,

    ChoiceSliderComponent,
    SmoothInDirective,
    LightboxComponent,
    ScreenTicketCountSelectorComponent,
    TicketCountGeneralAdmissionComponent,
    ScreenSeatsSelectedListComponent,
    PinchZoomComponent,
    VarDirective,
    CarouselSwipeDirective,
    PasswordInputComponent,
    DividerComponent,

    ScreenSizeDetectorComponent,
    RerenderDirective,
    FieldJumperDirective,
    HideOnErrorDirective,
    AddLanguagePipe,

    ReplaceClassDirective,
    OnlyLettersDirective
  ],
  imports: [
    CommonModule,
    TranslateModule.forChild(),
    TooltipModule.forRoot(),
    BsDatepickerModule.forRoot(),
    OwlCarouselModule,
    CarouselModule.forRoot(),
    NgPipesModule,
    ECommerceModule,
    SocialLoginModule,
    DigitDirective,
    ExtendedDatePipe,
    MoneyPipe,
    NoindexPageComponent,
    ScaleToFitDirective,
    MoneyDirective,
    PlaceholderImgDirective,
    FitTextToLinesDirective
  ],
  providers: [ExtendedDatePipe, EmbedVideoService, MoneyPipe],
  exports: [
    ChoiceSliderComponent,
    LightboxComponent,
    PinchZoomComponent,
    PasswordInputComponent,
    DividerComponent,
    PasswordInputComponent,
    ScreenSizeDetectorComponent,

    LazyImgDirective,
    PlaceholderImgDirective,
    PlayWatermarkDirective,
    FormErrorsDirective,
    SmoothInDirective,
    VarDirective,
    CarouselSwipeDirective,
    RerenderDirective,
    DigitDirective,
    FieldJumperDirective,
    HideOnErrorDirective,
    ReplaceClassDirective,
    OnlyLettersDirective,
    ScaleToFitDirective,

    DateTimePipe,
    ExtendedDatePipe,
    MoneyPipe,

    AddLanguagePipe,

    BsDatepickerModule,
    OwlCarouselModule,
    CarouselModule,
    ECommerceModule,
    SocialLoginModule,
    NoindexPageComponent,
    MoneyDirective,
    FitTextToLinesDirective
  ]
})
export class CoreLibraryModule {
  constructor(
    @Optional() @SkipSelf() parentModule: CoreLibraryModule,
    private injector: Injector
  ) {
    if (parentModule) {
      throw new Error('CoreLibraryModule is already loaded. Import it in the AppModule only');
    }
  }

  public static forRoot(environment: any): ModuleWithProviders<CoreLibraryModule> {
    return {
      ngModule: CoreLibraryModule,
      providers: [
        {
          provide: ENVIRONMENT_TOKEN,
          useValue: environment
        }
      ]
    };
  }

  public static forRootWithInterceptors(environment: any): ModuleWithProviders<CoreLibraryModule> {
    return {
      ngModule: CoreLibraryModule,
      providers: [
        {
          provide: ENVIRONMENT_TOKEN,
          useValue: environment
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: JwtTokenInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: JwtRefreshTokenInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AcceptLanguageInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: RequestInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ResponseInterceptor,
          multi: true
        }
      ]
    };
  }
}
